import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthActions } from '../../stores/authStore';
import { Header } from './Header';
import { Button } from '../ui/Button';
import { Separator } from '../ui/separator';
import { Card, CardContent } from '../ui/Card';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  FileText, 
  Settings, 
  LogOut,
  Sparkles,
  Copy,
  Check,
  CreditCard,
  FileCheck,
  RefreshCw,
  Receipt,
  Truck,
  BarChart3
} from 'lucide-react';

export default function CAPortalLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthActions(state => state.logout);
  const [copied, setCopied] = useState(false);

  const navigation = [
    { name: 'Overview', href: '/ca/dashboard', icon: LayoutDashboard },
    { name: 'Client Hub', href: '/ca/clients', icon: Users },
    { name: 'Compliance', href: '/ca/compliance', icon: Calendar },
    { name: 'Reports', href: '/ca/reports', icon: BarChart3 },
    { name: 'Documents', href: '/ca/documents', icon: FileText },
    { name: 'Subscription', href: '/ca/subscription', icon: CreditCard },
    { name: 'Settings', href: '/ca/settings', icon: Settings },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleInvite = () => {
    navigator.clipboard.writeText('https://soma.app/invite/ca/kavy-solutions');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex-shrink-0 fixed h-full z-10 flex flex-col">
        <div className="h-16 border-b border-slate-800 flex items-center px-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-lg shadow-slate-900/10">
              <span className="text-slate-900 font-bold text-lg leading-none pb-0.5">S</span>
            </div>
            <span className="text-xl font-bold text-white tracking-tight">soma.</span>
          </div>
        </div>

        <nav className="p-4 space-y-1 flex-1 overflow-y-auto scrollbar-hide" aria-label="Main navigation">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || (item.href !== '/ca/compliance' && location.pathname.startsWith(item.href));
            return (
              <Button
                key={item.name}
                asChild
                variant="ghost"
                className={`w-full justify-start gap-3 px-4 py-3 h-auto text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 hover:text-emerald-400' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <Link to={item.href} aria-current={isActive ? 'page' : undefined}>
                  <item.icon className="w-5 h-5" aria-hidden="true" />
                  {item.name}
                </Link>
              </Button>
            );
          })}
        </nav>

        <div className="p-4 space-y-4">
          <Separator className="bg-slate-800" />
          
          {/* Invite Banner */}
          <Card className="bg-gradient-to-br from-emerald-600 to-emerald-800 border-0 text-white relative overflow-hidden group">
            <CardContent className="p-4 relative z-10">
              <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity pointer-events-none">
                <Sparkles className="w-12 h-12" aria-hidden="true" />
              </div>
              <h4 className="font-bold text-sm mb-1 relative z-10">Grow Your Practice</h4>
              <p className="text-emerald-100 text-xs mb-3 relative z-10">Invite clients to Soma for seamless collaboration.</p>
              <Button
                onClick={handleInvite}
                variant="outline"
                size="sm"
                className="w-full bg-white/10 hover:bg-white/20 border-white/20 text-white hover:text-white text-xs font-medium"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Link Copied!' : 'Copy Invite Link'}
              </Button>
            </CardContent>
          </Card>

          <Button 
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 px-4 py-2 h-auto text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-5 h-5" aria-hidden="true" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 ml-64 flex flex-col">
        <Header />

        {/* Page Content */}
        <main className="flex-1 p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}